import { useState } from 'react';

const OBJECTIVES = ['Investment', 'Growth', 'Exit'];
const TIERS = ['Free', 'Lite', 'Pro'];

const variantKey = (obj, tier) => `${obj}-${tier}`;
const ALL_VARIANTS = OBJECTIVES.flatMap(obj => TIERS.map(tier => variantKey(obj, tier)));

const TIER_COLORS = { Free: '#4A67FF', Lite: '#038589', Pro: '#FC7785' };

const ExcelVariantModal = ({ open, parsedData, moduleKey, onConfirm, onClose }) => {
    // All unchecked by default
    const [selected, setSelected] = useState(new Set());

    if (!open) return null;

    const toggle = (key) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === ALL_VARIANTS.length) setSelected(new Set());
        else setSelected(new Set(ALL_VARIANTS));
    };

    const toggleObjective = (obj) => {
        const objKeys = TIERS.map(t => variantKey(obj, t));
        const allSelected = objKeys.every(k => selected.has(k));
        setSelected(prev => {
            const next = new Set(prev);
            if (allSelected) objKeys.forEach(k => next.delete(k));
            else objKeys.forEach(k => next.add(k));
            return next;
        });
    };

    const toggleTier = (tier) => {
        const tierKeys = OBJECTIVES.map(o => variantKey(o, tier));
        const allSelected = tierKeys.every(k => selected.has(k));
        setSelected(prev => {
            const next = new Set(prev);
            if (allSelected) tierKeys.forEach(k => next.delete(k));
            else tierKeys.forEach(k => next.add(k));
            return next;
        });
    };

    return (
        <div
            className="pf t0 l0 w100 h100 df aic jcc"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', zIndex: 9999 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="bg1 df fdc br20px shadow"
                style={{ width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}
            >
                {/* Header */}
                <div className="px2rem pt2rem pb1-5rem" style={{ borderBottom: '1px solid #DDE2F2' }}>
                    <h2 className="fs1-25rem fw600 c15 m0">Apply to Combinations</h2>
                    <p className="fs0-85rem c15 m0 mt0-25rem" style={{ opacity: 0.6 }}>
                        Select which objective × subscription tier combinations will receive
                        the {parsedData?.['totalSections'] || 0} sections and {parsedData?.['totalQuestions'] || 0} questions.
                    </p>
                </div>

                {/* Body */}
                <div className="px2rem py1-5rem df fdc" style={{ gap: '1.25rem' }}>
                    {/* Select all row */}
                    <div className="df jcsb aic">
                        <span className="fs0-85rem fw500 c15" style={{ opacity: 0.65 }}>
                            {selected.size} of {ALL_VARIANTS.length} selected
                        </span>
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="fs0-8rem fw600 cp br20px px1rem py0-35rem td300"
                            style={{ border: '1px solid #DDE2F2', background: '#fff', color: '#4A67FF' }}
                        >
                            {selected.size === ALL_VARIANTS.length ? 'Deselect all' : 'Select all'}
                        </button>
                    </div>

                    {/* Grid: 4 columns — label + 3 tiers */}
                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr', gap: '8px' }}>

                        {/* Top-left empty cell */}
                        <div />

                        {/* Tier header buttons */}
                        {TIERS.map(tier => {
                            const allTierSelected = OBJECTIVES.every(o => selected.has(variantKey(o, tier)));
                            return (
                                <button
                                    key={tier}
                                    type="button"
                                    onClick={() => toggleTier(tier)}
                                    className="tac fw600 fs0-85rem br8px py0-5rem td300 cp"
                                    style={{
                                        border:     `2px solid ${allTierSelected ? TIER_COLORS[tier] : '#DDE2F2'}`,
                                        background: allTierSelected ? TIER_COLORS[tier] : '#fafafa',
                                        color:      allTierSelected ? '#fff' : '#505050'
                                    }}
                                >
                                    {tier}
                                </button>
                            );
                        })}

                        {/* One row per objective */}
                        {OBJECTIVES.map(obj => {
                            const allObjSelected = TIERS.every(t => selected.has(variantKey(obj, t)));
                            return [
                                /* Objective label button */
                                <button
                                    key={`obj-${obj}`}
                                    type="button"
                                    onClick={() => toggleObjective(obj)}
                                    className="fw600 fs0-85rem br8px px0-75rem tal td300 cp"
                                    style={{
                                        border:     `2px solid ${allObjSelected ? '#002D63' : '#DDE2F2'}`,
                                        background: allObjSelected ? '#002D63' : '#fafafa',
                                        color:      allObjSelected ? '#fff' : '#505050',
                                        height:     '52px'
                                    }}
                                >
                                    {obj}
                                </button>,

                                /* Checkbox cell per tier */
                                ...TIERS.map(tier => {
                                    const key = variantKey(obj, tier);
                                    const isChecked = selected.has(key);
                                    const color = TIER_COLORS[tier];
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => toggle(key)}
                                            className="df aic jcc br8px td300 cp"
                                            style={{
                                                height:     '52px',
                                                border:     `2px solid ${isChecked ? color : '#DDE2F2'}`,
                                                background: isChecked ? color + '18' : '#fafafa'
                                            }}
                                        >
                                            {/* Checkbox square */}
                                            <span
                                                className="df aic jcc"
                                                style={{
                                                    width:        20,
                                                    height:       20,
                                                    borderRadius: 4,
                                                    border:       `2px solid ${isChecked ? color : '#AAACB5'}`,
                                                    background:   isChecked ? color : '#fff',
                                                    flexShrink:   0
                                                }}
                                            >
                                                {isChecked && (
                                                    <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                                        <path d="M1 4L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })
                            ];
                        })}
                    </div>

                    {/* Info note */}
                    <div className="df aic br8px p0-75rem" style={{ background: '#f0f2fd', border: '1px solid #c7d2fe', gap: '0.5rem' }}>
                        <i className="fas fa-info-circle" style={{ color: '#4A67FF', flexShrink: 0 }} />
                        <p className="fs0-8rem fw500 m0" style={{ color: '#3730a3' }}>
                            Sections will be numbered using module key <strong>{moduleKey || 'KEY'}</strong>
                            {' '}(e.g. {moduleKey || 'KEY'}001, {moduleKey || 'KEY'}001.1).
                            Each selected combination gets the full set of sections.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px2rem pb2rem df jcfe aic" style={{ gap: '0.75rem' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        className="br50px px1-5rem py0-75rem fw500 fs0-9rem cp td300"
                        style={{ border: '1px solid #DDE2F2', background: '#fff', color: '#505050' }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={selected.size === 0}
                        onClick={() => onConfirm([...selected])}
                        className="br50px px2rem py0-75rem fw600 fs0-9rem c1 td300"
                        style={{
                            background: selected.size === 0 ? '#D9D9D9' : '#4A67FF',
                            border:     'none',
                            cursor:     selected.size === 0 ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {selected.size === 0
                            ? 'Select combinations to continue'
                            : `Import to ${selected.size} combination${selected.size > 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExcelVariantModal;
